import { forwardRef, useImperativeHandle, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useDepositOnboarding } from '@/hooks/useDepositOnboarding';
import { OnboardingWizard } from './OnboardingWizard';

const SESSION_EXPIRED_MESSAGE = '인증 대기 시간이 지나 계좌 연동 창을 닫았어요.';

export interface OnboardingWizardHandle {
  open: (prefillAccount?: string) => void;
}

interface Props {
  // 연동 완료/세션 변동 후 부모 데이터 갱신(예: Deposits refresh, 배지 갱신).
  onChanged?: () => void;
}

// ★React.memo 금지(CLAUDE.md Anti-Bloat 규율 3).
export const OnboardingWizardContainer = forwardRef<OnboardingWizardHandle, Props>(
  function OnboardingWizardContainer({ onChanged }, ref) {
    const [open, setOpen] = useState(false);
    const [prefill, setPrefill] = useState<string | undefined>(undefined);
    const onb = useDepositOnboarding();
    const { phase, cancel } = onb;

    useImperativeHandle(ref, () => ({
      open: (prefillAccount?: string) => {
        setPrefill(prefillAccount);
        setOpen(true);
        onb.resume(); // 기존 세션 있으면 복구(멀티탭/새로고침), 없으면 input
      },
    }), [onb]);

    const handleOpenChange = useCallback((next: boolean) => {
      setOpen(next);
      if (!next) {
        onb.reset();      // 다음 오픈을 깨끗이
        onChanged?.();    // 닫을 때 부모 갱신(provisional 반영 등)
      }
    }, [onb, onChanged]);

    useEffect(() => {
      if (!open || phase !== 'session_expired') return;

      toast.info(SESSION_EXPIRED_MESSAGE);
      setOpen(false);
      void cancel(); // 서버 잠금도 즉시 해제. 실패해도 15분 lease가 최종 회수한다.
      onChanged?.();
    }, [cancel, onChanged, open, phase]);

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-md max-h-[85vh] overflow-y-auto"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            document.body.style.pointerEvents = '';
          }}
        >
          <DialogHeader>
            <DialogTitle>계좌 연동</DialogTitle>
            <DialogDescription className="sr-only">
              입금 계좌를 셀러헬퍼 게이트웨이에 SMS 통지 수신번호로 등록합니다.
            </DialogDescription>
          </DialogHeader>

          <OnboardingWizard
            phase={onb.phase}
            session={onb.session}
            authCode={onb.authCode}
            codeRemainingSec={onb.codeRemainingSec}
            registered={onb.registered}
            busyUntil={onb.busyUntil}
            errorMessage={onb.errorMessage}
            verifyRemainingSec={onb.verifyRemainingSec}
            start={onb.start}
            complete={onb.complete}
            cancel={onb.cancel}
            reset={onb.reset}
            prefillAccount={prefill}
            onClose={() => handleOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    );
  },
);
